// Character-sheet-style derived stats for a level-60 vanilla classic
// warrior. We compute Attack Power and melee Crit % from the gear-aggregated
// Strength / Agility that WCL's CombatantInfo gives us, plus the talents
// and current stance. WCL's vanilla CombatantInfo doesn't include AP /
// Crit directly so the user has to compute them — that's what the
// in-game character sheet shows.
//
// What's included:
//   - Strength → AP (warriors get 2 AP per Strength)
//   - Class + level base AP (level × 3 − 20 for warriors at 60)
//   - Agility → Crit (warriors get 1% crit per 20 Agility)
//   - Class base crit (~0.65% at level 60)
//   - Talents: Cruelty (1% crit per point, fury max 5/5)
//   - Stance: Berserker Stance (+3% crit) when its aura is on the player
//   - Gear: percentage Str/Agi multipliers (Blessing of Kings if present)
//     are already baked into parsedInfo.stats by WCL.
//
// What's NOT included (to be added later by parsing item tooltips):
//   - Flat AP from items ("Equip: +80 attack power")
//   - Flat melee crit % from items ("Equip: +1% crit")
// Display labels these as "估算" so the user knows the number is a lower
// bound on the actual character-sheet value.

import type { ParsedPlayerInfo } from './types';
import type { BuffEffects } from './raidBuffs';

const SPELL_BERSERKER_STANCE = 2458;

// Race+class base stats for a level-60 warrior. WCL doesn't expose race in
// vanilla CombatantInfo so we use a middle-of-the-pack approximation across
// the five warrior-eligible races (Human / Dwarf / Orc / Undead / Tauren).
// Values calibrated against the in-game character sheet for a Human warrior:
//   Human warrior @ 60: 120 str / 80 agi / 110 sta / 25 int / 30 spi
//   Tauren warrior:     +5 str / 0 agi / +1 sta vs Human
//   Undead warrior:     -1 str / -4 agi / -1 sta / -6 int / 0 spi
// So Human numbers are within ~5 for every race. The displayed base stats
// will land within ±5 of the in-town character sheet; remaining gap (if any)
// is from gear stats we couldn't parse from the tooltip.
export const WARRIOR_LEVEL_60_BASE = {
  strength: 120,
  agility: 80,
  stamina: 110,
  intellect: 25,
  spirit: 30,
};

export interface GearStatBonuses {
  // From "+N <stat>" lines on item tooltips, summed across all 19 pieces.
  // These are the gear's contribution; race+class base is added separately.
  str: number;
  agi: number;
  sta: number;
  int: number;
  spi: number;
  // From "Equip:" effect lines (flat AP, flat melee crit %).
  ap: number;
  crit: number;     // in PERCENT POINTS, not fraction
  // Sum of base armor across armor-bearing pieces ("565护甲" / "565 Armor").
  // Caller (main.ts) is responsible for zeroing this out on weapon slots —
  // vanilla weapon tooltips list a "护甲" line that does NOT contribute to
  // the player's armor pool, so summing weapons would over-count.
  armor: number;
}

export const ZERO_GEAR_STATS: GearStatBonuses = {
  str: 0, agi: 0, sta: 0, int: 0, spi: 0, ap: 0, crit: 0, armor: 0,
};

// Backwards-compatible alias for the older name we used for AP/crit only.
export type FlatGearBonuses = GearStatBonuses;

export interface CharSheetStats {
  attackPower: number;
  meleeCritChance: number;    // 0..1
  armor: number;              // base armor: gear armor + 2 × agility (no buffs / talents)
  inBerserkerStance: boolean;
  // Per-component crit breakdown so the UI can show "where each % comes
  // from". All values are in percent points (e.g. 7.20, not 0.072) so
  // they sum directly to (meleeCritChance * 100). User asked specifically
  // for transparency: "compute crit from gear + enchants + talents,
  // don't use WCL data" — this lets them verify each input.
  critBreakdown: {
    classBase: number;        // 0.65 (warrior level 60)
    fromAgility: number;      // baseStats.agility / 20
    fromCruelty: number;      // 0..5 (Cruelty talent rank, fury max 5/5)
    fromStance: number;       // 3 if Berserker, 0 otherwise
    fromGear: number;         // sum of "+N% crit" from items + enchants
  };
  // Base AP breakdown for parity with crit.
  apBreakdown: {
    classBase: number;        // level × 3 − 20 = 160 at 60
    fromStrength: number;     // 2 × baseStats.strength
    fromGear: number;         // sum of "+N attack power" from items + enchants
  };
  // The base attribute stats (gear + race+class baseline, before any
  // raid buff / consumable). Match what the in-game character sheet shows
  // when the player is unbuffed in town.
  baseStats: {
    strength: number;
    agility: number;
    stamina: number;
    intellect: number;
    spirit: number;
  };
}

// Sum gear stats + race+class base to produce unbuffed base stats. WCL's
// parsed.stats is already buffed (Mark of the Wild, Blessing of Kings,
// food, flasks, world buffs all apply pre-pull), so we DON'T touch it
// here — the gear-tooltip parse + race base recovers the in-town value.
export function computeCharSheetStats(
  parsed: ParsedPlayerInfo,
  level = 60,
  gearStats: GearStatBonuses = ZERO_GEAR_STATS,
  raceBase = WARRIOR_LEVEL_60_BASE,
): CharSheetStats {
  const baseStats = {
    strength: raceBase.strength + gearStats.str,
    agility: raceBase.agility + gearStats.agi,
    stamina: raceBase.stamina + gearStats.sta,
    intellect: raceBase.intellect + gearStats.int,
    spirit: raceBase.spirit + gearStats.spi,
  };

  // Warrior AP = 2 × Strength + (level × 3 − 20) + flat AP from gear.
  // Using BASE strength (not buffed) so the displayed AP matches what the
  // in-town character sheet shows.
  const attackPower = 2 * baseStats.strength + (level * 3 - 20) + gearStats.ap;

  // Armor: gear armor (parsed from each piece's "护甲" line, callers zero
  // it out for weapon slots since vanilla weapon armor doesn't add to the
  // player's armor pool) + 2 × agility (every class in vanilla gets 2 armor
  // per point of agility; this isn't a leather/mail/plate distinction).
  // Toughness (protection talent, +2/4/6/8/10% gear armor) is left out for
  // now since the user we're calibrating against is fury (no Toughness).
  const armor = gearStats.armor + 2 * baseStats.agility;

  // Crit chance: class base + Agility/20 + Cruelty + stance + flat gear crit.
  const classBaseCrit = 0.0065;
  const fromAgility = baseStats.agility / 20 / 100;
  const cruelty = parsed.inferredBuild['cruelty'] ?? 0;
  const fromCruelty = cruelty / 100;
  // Berserker stance gives +3% crit. WCL CombatantInfo only captures auras
  // present at the fight-start snapshot — if the warrior swapped stances
  // mid-pull or pulled in Battle stance, the Berserker aura won't appear
  // in parsed.auras even though they raided in Berserker for ~99% of the
  // fight. Fall back to the spec-based assumption: fury and arms warriors
  // ALWAYS raid in Berserker for PvE DPS (the +3% crit + +10% damage taken
  // tradeoff is a no-brainer for DPS), and protection warriors raid in
  // Defensive. So if the aura check fails, infer from spec rather than
  // defaulting to "no stance bonus" — which historically caused our
  // displayed crit to be 3% lower than the in-game character sheet.
  const inBerserkerStanceFromAura = parsed.auras.some((a) => a.abilityId === SPELL_BERSERKER_STANCE);
  const inBerserkerStanceBySpec   = parsed.spec === 'fury' || parsed.spec === 'arms';
  const inBerserkerStance = inBerserkerStanceFromAura || inBerserkerStanceBySpec;
  const stanceBonus = inBerserkerStance ? 0.03 : 0;
  const fromGearCrit = gearStats.crit / 100;

  return {
    attackPower,
    meleeCritChance: classBaseCrit + fromAgility + fromCruelty + stanceBonus + fromGearCrit,
    armor,
    inBerserkerStance,
    critBreakdown: {
      classBase:    classBaseCrit  * 100,   // 0.65
      fromAgility:  fromAgility    * 100,   // baseStats.agility / 20
      fromCruelty:  fromCruelty    * 100,   // cruelty rank %
      fromStance:   stanceBonus    * 100,   // 3 or 0
      fromGear:     fromGearCrit   * 100,   // gearStats.crit
    },
    apBreakdown: {
      classBase:    level * 3 - 20,         // 160 at 60
      fromStrength: 2 * baseStats.strength,
      fromGear:     gearStats.ap,
    },
    baseStats,
  };
}

// Stats with all selected buffs applied. Used by the "战斗中（含 buff）" view
// so the displayed numbers don't depend on WCL's CombatantInfo snapshot —
// vanilla CombatantInfo is unreliable for fight-start buff capture (world
// buffs / consumables / food / self Battle Shout often missing). Instead we
// take the base stats (race+class+gear+enchants), let the user toggle which
// buffs they actually had on, and recompute every dependent stat (AP, Crit,
// Armor) from the buffed attribute totals.
export interface BuffedStats {
  strength: number;
  agility: number;
  stamina: number;
  intellect: number;
  spirit: number;
  attackPower: number;
  meleeCritChance: number;     // 0..1
  armor: number;
  // Per-component buff contribution sums for the breakdown UI. All values
  // are PRE-multiplier (so the breakdown can show "+14 from MotW + ×1.10
  // from Kings" rather than "+38 from buffs" with no audit trail).
  buffContrib: {
    addStr: number; addAgi: number; addSta: number; addInt: number; addSpi: number;
    addAp: number; addCrit: number; addArmor: number;
    attrMult: number;          // product of all attrMult fields (1 if no Kings/Zandalar)
  };
}

export function computeBuffedStats(
  cs: CharSheetStats,
  parsed: ParsedPlayerInfo,
  gearStats: GearStatBonuses,
  buffs: ReadonlyArray<BuffEffects>,
  level = 60,
): BuffedStats {
  // Sum additive contributions across all selected buffs. attrMult fields
  // chain MULTIPLICATIVELY so Kings (1.10) + Zandalar (1.15) = 1.265× total
  // (each multiplier applies independently to the same base+additive total).
  let addStr = 0, addAgi = 0, addSta = 0, addInt = 0, addSpi = 0;
  let addAp = 0, addCrit = 0, addArmor = 0;
  let attrMult = 1;
  for (const e of buffs) {
    if (e.str)   addStr   += e.str;
    if (e.agi)   addAgi   += e.agi;
    if (e.sta)   addSta   += e.sta;
    if (e.int)   addInt   += e.int;
    if (e.spi)   addSpi   += e.spi;
    if (e.ap)    addAp    += e.ap;
    if (e.crit)  addCrit  += e.crit;
    if (e.armor) addArmor += e.armor;
    if (e.attrMult) attrMult *= e.attrMult;
  }

  // Apply additive THEN multiplicative on attribute stats. Floor matches
  // what the in-game character sheet displays (it truncates, not rounds).
  const buffedStr = Math.floor((cs.baseStats.strength + addStr) * attrMult);
  const buffedAgi = Math.floor((cs.baseStats.agility  + addAgi) * attrMult);
  const buffedSta = Math.floor((cs.baseStats.stamina  + addSta) * attrMult);
  const buffedInt = Math.floor((cs.baseStats.intellect+ addInt) * attrMult);
  const buffedSpi = Math.floor((cs.baseStats.spirit   + addSpi) * attrMult);

  // Recompute dependent stats from the buffed attributes. AP and Crit use
  // the SAME formula as base view (class base + str/agi + cruelty + stance
  // + flat gear/enchant) but with the buffed attribute values — plus the
  // additive-AP / additive-Crit contributions from buffs themselves
  // (Battle Shout, Trueshot, Rallying Cry, Mongoose, etc.).
  const buffedAP = 2 * buffedStr + (level * 3 - 20) + gearStats.ap + addAp;

  const classBaseCrit = 0.0065;
  const cruelty = (parsed.inferredBuild['cruelty'] ?? 0) / 100;
  const stanceBonus = cs.inBerserkerStance ? 0.03 : 0;
  const buffedCrit = classBaseCrit
    + buffedAgi / 20 / 100
    + cruelty
    + stanceBonus
    + gearStats.crit / 100
    + addCrit / 100;

  // Armor: gear armor + 2 × buffed agility + flat-armor buff contributions
  // (Greater Stoneshield, Devotion Aura — none of which are in our catalog
  // by default, but the field is here for completeness when we add them).
  const buffedArmor = gearStats.armor + 2 * buffedAgi + addArmor;

  return {
    strength: buffedStr, agility: buffedAgi, stamina: buffedSta,
    intellect: buffedInt, spirit: buffedSpi,
    attackPower: buffedAP, meleeCritChance: buffedCrit, armor: buffedArmor,
    buffContrib: {
      addStr, addAgi, addSta, addInt, addSpi,
      addAp, addCrit, addArmor, attrMult,
    },
  };
}

// Parse a wowhead item tooltip's HTML for ALL stat contributions:
//   - +N Strength / Agility / Stamina / Intellect / Spirit
//   - +N to all stats (sum into all five)
//   - Equip: +N attack power
//   - Equip: +N% chance to crit
// Handles both EN and ZH wording. Summed across all 19 gear pieces in
// main.ts to produce a slot's full GearStatBonuses.
//
// Patterns we recognise (case-insensitive for English):
//   Equip: Increases (your? melee?) attack power by N        EN
//   +N attack power                                          EN, rare
//   装备：使近战?攻击强度提高 N 点                            ZH
//   Equip: Improves your chance to get a critical strike by N%   EN
//   Equip: Increases your chance to get a critical strike by N%  EN
//   +N% Chance to Critically Hit                             EN, vanilla classic on some trinkets
//   装备：使你的近战?暴击几率提高 N%                          ZH
//
// Spell-only crit is rare on warrior-targeted gear; we don't try to
// distinguish "+1% to spell crit" from "+1% to melee crit" because vanilla
// classic gear with spell crit is almost always healer-only and the user
// wouldn't be wearing it on a fury warrior anyway.
export function extractGearStats(tooltipHtml: string): GearStatBonuses {
  let text = tooltipHtml
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/[ \t]+/g, ' ');

  // Strip "Use:" / "使用：" lines (on-use trinket effects, temporary buffs).
  // Without this, Slayer's Crest's "使用：攻击强度提高 260 点持续 20 秒" is
  // matched by our /攻击强度提高 N/ pattern and gets added on top of the
  // legitimate "装备：+64 攻击强度" line, inflating the AP estimate by 260+.
  text = text
    .replace(/Use\s*:[^.\n]*\.?/gi, ' ')
    .replace(/使用\s*[:：][^。\n]*。?/g, ' ');

  // Strip on-hit / on-cast PROC sentences with a duration clause. Weapon
  // enchants like Crusader say:
  //   "在攻击时经常性地为你...，并使你的力量提高100点，持续15秒。"
  //   "...increase your Strength by 100 for 15 sec."
  // Without this filter, the +100 STR temporary buff would be parsed as if
  // it were permanent — and a fury warrior with Crusader on BOTH weapons
  // would get +200 false strength.
  // The filter targets any sentence containing "持续\s*\d+\s*秒" (ZH) or
  // "for/lasting \d+ sec" (EN) and removes the whole sentence.
  text = text
    .replace(/[^。\n]*持续\s*\d+\s*秒[^。\n]*。?/g, ' ')
    .replace(/[^.\n]*\b(?:for|lasting)\s+\d+\s*sec(?:ond)?s?\b[^.\n]*\.?/gi, ' ');

  // Strip the set-bonus block. Tier-set tooltips list every (N) set bonus
  // as plain text inside the noscript SSR HTML, e.g.
  //   "英勇 (1/8) 英勇长靴 英勇胸部 ... (2) 组合 : +40 攻击强度。
  //    (4) 组合 : ... (6) 组合 : +8 所有魔法抗性。 (8) 组合 : +200 护甲。"
  // With only 1/8 of the set equipped NONE of these bonuses are active —
  // but our naive "+40 攻击强度" regex would happily count them as gear AP.
  // Same problem for warrior T2 (愤怒) "(3) 组合 : 战斗怒吼...提高30点" —
  // would falsely match our "攻击强度提高 N" pattern.
  //
  // Cut everything from the first "(N) 组合 :" / "(N) Set :" pattern
  // onward. The set name+counter line ("英勇 (1/8)" / "Heroism (1/8)")
  // sits BEFORE these bonuses, so we lose the member-name listing too —
  // which is fine, the member names are decorative noise we don't parse.
  text = text
    .replace(/\(\d+\)\s*组合\s*[:：][\s\S]*$/, ' ')
    .replace(/\(\d+\)\s*Set\s*:[\s\S]*$/i, ' ');

  let str = 0, agi = 0, sta = 0, int_ = 0, spi = 0, ap = 0, crit = 0, armor = 0;

  // ============================================================
  // Item tooltip primary stats — "+N <stat>" format.
  // ============================================================
  // EN
  for (const m of text.matchAll(/\+(\d+)\s+(?:to\s+)?Strength\b/gi))  str  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/\+(\d+)\s+(?:to\s+)?Agility\b/gi))   agi  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/\+(\d+)\s+(?:to\s+)?Stamina\b/gi))   sta  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/\+(\d+)\s+(?:to\s+)?Intellect\b/gi)) int_ += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/\+(\d+)\s+(?:to\s+)?Spirit\b/gi))    spi  += parseInt(m[1]!, 10);
  // ZH item tooltip: +N 力量
  for (const m of text.matchAll(/\+(\d+)\s*力量/g)) str  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/\+(\d+)\s*敏捷/g)) agi  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/\+(\d+)\s*耐力/g)) sta  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/\+(\d+)\s*智力/g)) int_ += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/\+(\d+)\s*精神/g)) spi  += parseInt(m[1]!, 10);
  // "+N to all stats" (Devilsaur, Songflower-themed gear, etc.)
  for (const m of text.matchAll(/\+(\d+)\s+(?:to\s+)?[Aa]ll\s+(?:[Ss]tats|[Aa]ttributes)/g)) {
    const v = parseInt(m[1]!, 10);
    str += v; agi += v; sta += v; int_ += v; spi += v;
  }
  for (const m of text.matchAll(/\+(\d+)\s*(?:全部属性|所有属性)/g)) {
    const v = parseInt(m[1]!, 10);
    str += v; agi += v; sta += v; int_ += v; spi += v;
  }

  // ============================================================
  // Enchant spell description formats:
  //   ZH "力量+9"            (e.g. Enchant Bracer - Superior Strength)
  //   ZH "力量提高 8 点"      (e.g. Lesser Arcanum of Voracity)
  //   ZH "所有属性+4"         (Enchant Chest - Greater Stats)
  //   EN "Strength by N"     (e.g. "increase strength by 9")
  //   EN "to Strength by N"  (rarer phrasing)
  // ============================================================
  // ZH stat-prefix + "+N"
  for (const m of text.matchAll(/力量\+(\d+)/g)) str  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/敏捷\+(\d+)/g)) agi  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/耐力\+(\d+)/g)) sta  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/智力\+(\d+)/g)) int_ += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/精神\+(\d+)/g)) spi  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/所有属性\+(\d+)/g)) {
    const v = parseInt(m[1]!, 10);
    str += v; agi += v; sta += v; int_ += v; spi += v;
  }
  // ZH "X提高N点" (skip if "属性提高" — handled by all-stats above)
  for (const m of text.matchAll(/力量提高\s*(\d+)\s*点?/g)) str  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/敏捷提高\s*(\d+)\s*点?/g)) agi  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/耐力提高\s*(\d+)\s*点?/g)) sta  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/智力提高\s*(\d+)\s*点?/g)) int_ += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/精神提高\s*(\d+)\s*点?/g)) spi  += parseInt(m[1]!, 10);
  // EN "<Stat> by N" (enchant spell descriptions)
  for (const m of text.matchAll(/Strength\s+by\s+(\d+)/gi))  str  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/Agility\s+by\s+(\d+)/gi))   agi  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/Stamina\s+by\s+(\d+)/gi))   sta  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/Intellect\s+by\s+(\d+)/gi)) int_ += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/Spirit\s+by\s+(\d+)/gi))    spi  += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/[Aa]ll\s+(?:[Ss]tats|[Aa]ttributes)\s+by\s+(\d+)/g)) {
    const v = parseInt(m[1]!, 10);
    str += v; agi += v; sta += v; int_ += v; spi += v;
  }

  // EN AP: "(Increases? your? melee?) attack power by N"
  for (const m of text.matchAll(/(?:increases?|improves?)\s+(?:your\s+)?(?:melee\s+)?attack\s+power\s+by\s+(\d+)/gi)) {
    ap += parseInt(m[1]!, 10);
  }
  // EN AP: "+N (to)? (melee )?attack power"
  for (const m of text.matchAll(/\+(\d+)\s+(?:to\s+)?(?:melee\s+)?attack\s+power/gi)) {
    ap += parseInt(m[1]!, 10);
  }
  // ZH AP — actual wowhead-cn formats observed:
  //   "+26 攻击强度"            (most common on rings/trinkets/chest)
  //   "攻击强度提高 80 点"        (older / paraphrased)
  //   "攻击强度+26点"            (Might of the Scourge enchant — `+` AFTER stat!)
  // The third form is essential for shoulder enchants like 天灾的威严 /
  // 战歌之力 — without it we lose +26 AP per affected character.
  for (const m of text.matchAll(/\+(\d+)\s*攻击强度/g)) {
    ap += parseInt(m[1]!, 10);
  }
  for (const m of text.matchAll(/(?:近战)?攻击强度提高\s*(\d+)/g)) {
    ap += parseInt(m[1]!, 10);
  }
  for (const m of text.matchAll(/攻击强度\+(\d+)\s*点?/g)) {
    ap += parseInt(m[1]!, 10);
  }

  // EN crit: "(Improves|Increases) your chance to (get a) critical strike (with X)? by N%"
  for (const m of text.matchAll(/(?:improves|increases)\s+(?:your\s+)?chance\s+to\s+(?:get\s+a\s+)?critical(?:ly)?\s+(?:hit|strike)[^.]*?\s+by\s+(\d+(?:\.\d+)?)\s*%/gi)) {
    crit += parseFloat(m[1]!);
  }
  // EN crit: "+N% Chance to (Critically Hit|critical strike)"
  for (const m of text.matchAll(/\+(\d+(?:\.\d+)?)\s*%\s+chance\s+to\s+critical(?:ly)?\s+(?:hit|strike)/gi)) {
    crit += parseFloat(m[1]!);
  }
  // ZH crit — actual wowhead-cn wording:
  //   "使你造成爆击的几率提高 2%。"   (note 爆击 not 暴击 — different char)
  //   "(近战)?暴击几率提高 2%"
  for (const m of text.matchAll(/(?:造成)?(?:爆击|暴击)(?:的几率|几率|率)?提高\s*(\d+(?:\.\d+)?)\s*%/g)) {
    crit += parseFloat(m[1]!);
  }

  // ============================================================
  // Armor — base armor on the item itself.
  // ============================================================
  // ZH wowhead noscript format examples:
  //   "565护甲" (helm/shoulders/chest/etc., NO + sign and NO whitespace)
  //   "+50 护甲"  (cloak enchant Lesser Armor)
  //   "598护甲"  (legs)
  // EN format:
  //   "565 Armor"
  //   "+50 Armor"
  // Set bonuses like "(8) 组合 : +200 护甲" are stripped above.
  // Use: lines with armor are stripped above.
  // The negative lookahead (?![^护]*提高) tries to avoid matching numbers
  // inside "护甲提高 N%" / "armor by N" effect descriptions, but in
  // practice the proc/duration filter handles those cases. Weapons have
  // a stray "140护甲" line in their tooltip; the caller (main.ts) zeros
  // out armor for weapon slots so weapon "armor" doesn't double-count.
  for (const m of text.matchAll(/\+?(\d+)\s*护甲(?!\D*?:)/g)) armor += parseInt(m[1]!, 10);
  for (const m of text.matchAll(/\+?(\d+)\s*[Aa]rmor\b/g))    armor += parseInt(m[1]!, 10);

  return { str, agi, sta, int: int_, spi, ap, crit, armor };
}

// Backwards-compatible alias — same shape, different name.
export const extractFlatBonuses = extractGearStats;
